import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { AuthProvider } from "./context/AuthContext";
import { SocketProvider } from "./context/SocketContext";
import ProtectedRoute from "./components/common/ProtectedRoute";
import Layout from "./components/layout/Layout";
import AppTheme from "./theme/AppTheme";

// Pages
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import UploadCall from "./pages/UploadCall";
import TeamAnalysis from "./pages/TeamAnalysis";
import SalespersonDetail from "./pages/SalespersonDetail";
import CallDetail from "./pages/CallDetail";
import AISettings from "./pages/AISettings";
import EmployeeManagement from "./pages/EmployeeManagement";
import ProfileManagement from "./pages/ProfileManagement";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Register from "./pages/Register";
import PaymentSuccess from "./pages/PaymentSuccess";
import AccountManagement from "./pages/AccountManagement";
import LandingPage from "./pages/LandingPage";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";

// Route guard components
const ManagerRoute: React.FC<{ children: React.ReactElement }> = ({
  children,
}) => {
  return <ProtectedRoute requireManager>{children}</ProtectedRoute>;
};

const SalespersonRoute: React.FC<{ children: React.ReactElement }> = ({
  children,
}) => {
  return <ProtectedRoute requireSalesperson>{children}</ProtectedRoute>;
};

function App() {
  return (
    <AppTheme>
      <AuthProvider>
        <SocketProvider>
          <Router>
            <div className="App">
              <Toaster
                position="top-right"
                toastOptions={{
                  duration: 4000,
                  style: {
                    background: "#363636",
                    color: "#fff",
                  },
                  success: {
                    duration: 3000,
                    iconTheme: {
                      primary: "#4ade80",
                      secondary: "#fff",
                    },
                  },
                  error: {
                    duration: 5000,
                    iconTheme: {
                      primary: "#f87171",
                      secondary: "#fff",
                    },
                  },
                }}
              />

              <Routes>
                {/* Public routes */}
                <Route path="/" element={<LandingPage />} />
                <Route path="/login" element={<Login />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/register" element={<Register />} />
                <Route path="/payment-success" element={<PaymentSuccess />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />

                {/* Manager-only routes */}
                <Route
                  path="/dashboard"
                  element={
                    <ManagerRoute>
                      <Layout>
                        <Dashboard />
                      </Layout>
                    </ManagerRoute>
                  }
                />

                <Route
                  path="/team"
                  element={
                    <ManagerRoute>
                      <Layout>
                        <TeamAnalysis />
                      </Layout>
                    </ManagerRoute>
                  }
                />

                <Route
                  path="/ai-settings"
                  element={
                    <ManagerRoute>
                      <Layout>
                        <AISettings />
                      </Layout>
                    </ManagerRoute>
                  }
                />

                <Route
                  path="/employee-management"
                  element={
                    <ManagerRoute>
                      <Layout>
                        <EmployeeManagement />
                      </Layout>
                    </ManagerRoute>
                  }
                />

                {/* Shared routes - both manager and salesperson can access */}
                <Route
                  path="/upload"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <UploadCall />
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/call/:id"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <CallDetail />
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/salesperson/:id"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <SalespersonDetail />
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                {/* Salesperson-only routes */}
                <Route
                  path="/profile"
                  element={
                    <SalespersonRoute>
                      <Layout>
                        <ProfileManagement />
                      </Layout>
                    </SalespersonRoute>
                  }
                />

                <Route
                  path="/account"
                  element={
                    <ManagerRoute>
                      <Layout>
                        <AccountManagement />
                      </Layout>
                    </ManagerRoute>
                  }
                />

                {/* Fallback route */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </div>
          </Router>
        </SocketProvider>
      </AuthProvider>
    </AppTheme>
  );
}

export default App;
